import * as THREE from 'three';
import { basic, emis, leather, metal, std } from '../materials.ts';
import { G, M, V, glowSprite, limb } from '../prims.ts';
import { kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * El Fuelle (the Chasm's Dead Forge guardian, docs/SIMA.md «Guardianes»): «Cuando sopla, ponete detrás de un
 * yunque.» A forge-construct of the dwarven clan that never came back: squat and broad as a forge, an iron lid
 * for shoulders over a chest that is a great leather bellows banded with iron, a dwarf's iron face with a beard of
 * plates and chains, and under it a grated furnace mouth. Two chimneys smoke on its back; its right fist is a
 * forge hammer, its left a pair of tongs; short iron legs with broad feet. Fire in its eyes, its mouth, the runes
 * on its lid.
 *
 * How the integrator drives it (anim/creatures/bellows.ts): 'inhale' (2.0 s, the sim's inhale) — the bellows
 * draw open (the lid rises, the folds part and swell), it rears back, the grate opens; 'blow' (1.6 s) — the
 * bellows slam shut, it lurches forward and the fire pours out of its mouth (the `fire` bone, built tiny, from
 * 0.12 s to 1.2 s).
 *
 * Bones: body (the lower plate and the hips), legs (legL, legR), fold1–fold3 (the bellows' folds, bottom to top),
 * top (the lid, shoulders, face, chimneys, arms), jaw (the lower grate), fire, arms (arm, fore). Faces +Z.
 */

/** How small the fire is built (the blow clip scales `fire` by 1 / BELLOWS_FIRE_REST). */
export const BELLOWS_FIRE_REST = 0.02;
export const BELLOWS_HEIGHT = 2.0;
/** How far the folds sit apart; each fold is taller than that, so they part without gaps up to ~0.08 a fold. */
export const BELLOWS_FOLD = 0.14;

export const bellows = (o?: BaseOpts & { blowing?: boolean }): Model => {
  const c = begin(o, 1.15, 9091);
  const { g, rng } = c;
  const k4 = kit(rng);

  const iron = metal('#474b52', { roughness: 0.55 });
  const ironD = std('#17181b', { roughness: 0.65, metalness: 0.5, flatShading: true });
  const hide = leather('#3b2924', { roughness: 0.75, bumpScale: 0.04 });
  const fire = emis('#ffb894', '#ff5a3a', 2.2);
  const hot = emis('#ffc29a', '#ff6a2a', 2.8);
  const fireT = emis('#ff4a2a', '#ff3a1a', 2.2, { transparent: true, opacity: 0.7, depthWrite: false });
  const smoke = basic('#1d1b1b', 0.42);

  const rivets = (parent: THREE.Object3D, r: number, y: number, n: number): void => {
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      M(new THREE.SphereGeometry(0.025, 4, 3), ironD, parent).position.set(Math.cos(a) * r, y, Math.sin(a) * r * 0.86);
    }
  };

  /* ---------- legs: short iron columns on broad feet ---------- */
  const legs: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const legB = joint(g, V(k * 0.42, 0.66, 0), `leg${s}`);
    const lg = legB.abs;
    limb(lg, V(k * 0.42, 0.68, 0), V(k * 0.44, 0.16, 0.03), 0.17, 0.2, iron, 8);
    const knee = M(new THREE.BoxGeometry(0.3, 0.2, 0.1), ironD, lg);
    knee.position.set(k * 0.44, 0.42, 0.2);
    knee.rotation.x = -0.15;
    const foot = M(new THREE.BoxGeometry(0.4, 0.14, 0.56), ironD, lg);
    foot.position.set(k * 0.45, 0.07, 0.1);
    for (let i = -1; i <= 1; i++) {
      const toe = M(new THREE.BoxGeometry(0.11, 0.1, 0.12), iron, lg);
      toe.position.set(k * 0.45 + i * 0.13, 0.06, 0.42);
    }
    legs[`leg${s}`] = legB.p;
  }

  /* ---------- the lower plate ---------- */
  const bodyB = joint(g, V(0, 0.72, 0), 'body');
  const body = bodyB.abs;
  const low = M(new THREE.CylinderGeometry(0.84, 0.78, 0.16, 16), iron, body);
  low.position.y = 0.74;
  low.scale.z = 0.86;
  rivets(body, 0.82, 0.8, 16);
  // Skirt plates hanging round it.
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * Math.PI * 2 + 0.31;
    if (Math.abs(Math.cos(a)) > 0.86) continue;
    const pl = M(new THREE.BoxGeometry(0.3, 0.22, 0.04), ironD, body);
    pl.position.set(Math.cos(a) * 0.8, 0.6, Math.sin(a) * 0.7);
    pl.rotation.y = -a + Math.PI / 2;
    pl.rotateX(0.2);
  }

  /* ---------- the bellows: three folds of leather banded with iron ---------- */
  const folds: THREE.Group[] = [];
  let parent: THREE.Object3D = body;
  for (let i = 0; i < 3; i++) {
    const y = 0.82 + i * BELLOWS_FOLD;
    const fB = joint(parent, V(0, y, 0), `fold${i + 1}`);
    const f = fB.abs;
    const leaf = M(new THREE.CylinderGeometry(0.72, 0.72, BELLOWS_FOLD * 1.6, 16, 2, true), hide, f);
    leaf.position.y = y + BELLOWS_FOLD * 0.55;
    leaf.scale.z = 0.86;
    // The fold's belly: pushed out at its middle ring.
    const pos = leaf.geometry.attributes.position as THREE.BufferAttribute;
    for (let j = 0; j < pos.count; j++) if (Math.abs(pos.getY(j)) < 0.01) pos.setXYZ(j, pos.getX(j) * 1.1, 0, pos.getZ(j) * 1.1);
    leaf.geometry.computeVertexNormals();
    const band = M(new THREE.TorusGeometry(0.73, 0.03, 4, 22), iron, f);
    band.rotation.x = Math.PI / 2;
    band.position.y = y + BELLOWS_FOLD;
    band.scale.y = 0.86;
    folds.push(fB.p);
    parent = f;
  }

  /* ---------- the lid: shoulders, face, beard, chimneys and arms ---------- */
  const topB = joint(parent, V(0, 1.26, 0), 'top');
  const top = topB.abs;
  const lid = M(new THREE.CylinderGeometry(0.8, 0.86, 0.16, 16), iron, top);
  lid.position.y = 1.32;
  lid.scale.z = 0.86;
  rivets(top, 0.84, 1.38, 16);
  // Runes of the clan round the lid, glowing.
  for (let i = 0; i < 7; i++) {
    const a = -1.2 + i * 0.4;
    const r = M(new THREE.BoxGeometry(0.1, 0.07, 0.012), fire, top);
    r.position.set(Math.sin(a) * 0.82, 1.33, Math.cos(a) * 0.72);
    r.rotation.y = a;
  }
  // The crown of the lid behind the face.
  const dome = M(new THREE.SphereGeometry(0.62, 14, 6, 0, Math.PI * 2, 0, Math.PI / 2), ironD, top);
  dome.position.set(0, 1.4, -0.08);
  dome.scale.set(1, 0.45, 0.8);
  // Shoulders: heavy pauldrons.
  for (const k of [-1, 1]) {
    const pd = M(new THREE.SphereGeometry(0.3, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2), iron, top);
    pd.position.set(k * 0.78, 1.42, 0);
    pd.scale.set(1, 0.8, 1);
    rivets(pd, 0.25, 0.04, 6);
  }
  // The face: a dwarf's iron helm-mask set forward on the lid.
  const helm = M(new THREE.CylinderGeometry(0.27, 0.3, 0.42, 10), iron, top);
  helm.position.set(0, 1.66, 0.42);
  const brow = M(new THREE.BoxGeometry(0.6, 0.09, 0.14), ironD, top);
  brow.position.set(0, 1.76, 0.68);
  brow.rotation.x = 0.2;
  for (const k of [-1, 1]) {
    const eye = M(new THREE.BoxGeometry(0.13, 0.035, 0.02), hot, top);
    eye.position.set(k * 0.12, 1.69, 0.72);
    eye.rotation.z = k * 0.2;
    glowSprite(top, '#ff7a3a', 0.28, V(k * 0.12, 1.69, 0.76), 0.85);
  }
  const noseG = M(new THREE.BoxGeometry(0.06, 0.2, 0.08), ironD, top);
  noseG.position.set(0, 1.64, 0.73);
  // Horns of the helm, short and blunt.
  for (const k of [-1, 1]) {
    const h = M(new THREE.ConeGeometry(0.07, 0.3, 6), ironD, top);
    h.position.set(k * 0.32, 1.86, 0.4);
    h.rotation.z = -k * 0.9;
  }
  // The furnace mouth: a dark grated opening with the fire inside; the lower grate hinged.
  const maw = M(new THREE.BoxGeometry(0.34, 0.2, 0.1), ironD, top);
  maw.position.set(0, 1.47, 0.66);
  const inside = M(new THREE.BoxGeometry(0.28, 0.14, 0.04), fire, top);
  inside.position.set(0, 1.47, 0.7);
  glowSprite(top, '#ff5a3a', 0.8, V(0, 1.47, 0.78), 0.7);
  for (let i = 0; i < 4; i++) {
    const bar = M(new THREE.BoxGeometry(0.025, 0.08, 0.03), iron, top);
    bar.position.set(-0.1 + i * 0.066, 1.53, 0.73);
  }
  const jawB = joint(top, V(0, 1.45, 0.72), 'jaw');
  const jw = jawB.abs;
  for (let i = 0; i < 4; i++) {
    const bar = M(new THREE.BoxGeometry(0.025, 0.09, 0.03), iron, jw);
    bar.position.set(-0.1 + i * 0.066, 1.41, 0.73);
  }
  // The beard: plates of iron hanging over the bellows, braided with chain, a ring at each end.
  for (let i = 0; i < 5; i++) {
    const x = (i - 2) * 0.13;
    const len = 0.5 - Math.abs(i - 2) * 0.08;
    const pl = M(new THREE.BoxGeometry(0.11, len, 0.05), i % 2 ? ironD : iron, jw);
    pl.position.set(x, 1.36 - len / 2, 0.74 - Math.abs(i - 2) * 0.03);
    pl.rotation.z = (i - 2) * 0.06;
    const rg = M(new THREE.TorusGeometry(0.04, 0.012, 4, 8), iron, jw);
    rg.position.set(x, 1.34 - len, 0.75 - Math.abs(i - 2) * 0.03);
  }
  // Chimneys on the back, smoking, embers at their mouths.
  for (const k of [-1, 1]) {
    const ch = M(new THREE.CylinderGeometry(0.1, 0.12, 0.56, 8), iron, top);
    ch.position.set(k * 0.34, 1.64, -0.52);
    ch.rotation.set(-0.3, 0, -k * 0.1);
    const cap = M(new THREE.CylinderGeometry(0.14, 0.11, 0.08, 8), ironD, top);
    cap.position.set(k * 0.37, 1.92, -0.6);
    cap.rotation.x = -0.3;
    const ember = M(new THREE.CircleGeometry(0.09, 8), hot, top);
    ember.position.set(k * 0.37, 1.965, -0.61);
    ember.rotation.x = -Math.PI / 2 - 0.3;
    glowSprite(top, '#ff6a2a', 0.45, V(k * 0.37, 2.0, -0.62), 0.5);
    for (let j = 0; j < 3; j++) {
      const p = M(new THREE.SphereGeometry(0.1 + j * 0.05, 6, 4), smoke, top);
      p.position.set(k * (0.38 + j * 0.05), 2.06 + j * 0.16, -0.66 - j * 0.1);
    }
  }

  /* ---------- the fire (built tiny) ---------- */
  const fireB = joint(top, V(0, 1.47, 0.76), 'fire');
  {
    const kf = o?.blowing ? 1 : BELLOWS_FIRE_REST;
    const L = 3.2 * kf, R = 1.25 * kf;
    const fg = G(fireB.abs, 0, 1.47, 0.76);
    fg.rotation.x = Math.PI / 2 + 0.3;
    const outer = new THREE.ConeGeometry(R, L, 10, 1, true);
    outer.translate(0, -L / 2, 0);
    outer.rotateX(Math.PI);
    M(outer, fireT, fg);
    const inner = new THREE.ConeGeometry(R * 0.45, L * 0.75, 8, 1, true);
    inner.translate(0, -L * 0.375, 0);
    inner.rotateX(Math.PI);
    M(inner, hot, fg);
  }

  /* ---------- arms: the hammer on the right, the tongs on the left ---------- */
  const arms: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const armB = joint(top, V(k * 0.86, 1.42, 0), `arm${s}`);
    const sh = armB.abs;
    limb(sh, V(k * 0.9, 1.4, 0), V(k * 1.02, 1.0, 0.1), 0.15, 0.13, iron, 8);
    const foreB = joint(sh, V(k * 1.02, 1.0, 0.1), `fore${s}`);
    const fo = foreB.abs;
    M(new THREE.SphereGeometry(0.14, 8, 6), ironD, fo).position.set(k * 1.02, 1.0, 0.1);
    limb(fo, V(k * 1.02, 1.0, 0.1), V(k * 1.05, 0.62, 0.24), 0.13, 0.16, iron, 8);
    const cuff = M(new THREE.CylinderGeometry(0.18, 0.18, 0.1, 8), ironD, fo);
    cuff.position.set(k * 1.05, 0.66, 0.22);
    if (k > 0) {
      // The hammer: a forge hammer's head for a fist, runes hot in its face.
      const hh = M(new THREE.BoxGeometry(0.42, 0.28, 0.3), ironD, fo);
      hh.position.set(1.06, 0.46, 0.28);
      const face = M(new THREE.BoxGeometry(0.44, 0.3, 0.04), iron, fo);
      face.position.set(1.06, 0.46, 0.45);
      for (let i = 0; i < 3; i++) {
        const r = M(new THREE.BoxGeometry(0.06, 0.12, 0.012), fire, fo);
        r.position.set(1.06 + (i - 1) * 0.1, 0.46, 0.475);
      }
      glowSprite(fo, '#ff5a3a', 0.4, V(1.06, 0.46, 0.5), 0.35);
    } else {
      // The tongs: two long jaws of black iron.
      for (const j of [-1, 1]) {
        const p0 = V(-1.05 + j * 0.06, 0.58, 0.26), p1 = V(-1.06 + j * 0.1, 0.32, 0.36), p2 = V(-1.06 + j * 0.03, 0.12, 0.44);
        k4.tube(fo, [p0, p1, p2], 0.035, ironD, 5);
      }
    }
    arms[`arm${s}`] = armB.p;
    arms[`fore${s}`] = foreB.p;
  }

  // The forge's glow round its feet.
  glowSprite(g, '#ff5a3a', 2.0, V(0, 0.1, 0.2), 0.12);

  return done(c, BELLOWS_HEIGHT, () => undefined, undefined, bonesOf(c, {
    body: bodyB.p, legL: legs.legL!, legR: legs.legR!, fold1: folds[0]!, fold2: folds[1]!, fold3: folds[2]!,
    top: topB.p, jaw: jawB.p, fire: fireB.p, armL: arms.armL!, foreL: arms.foreL!, armR: arms.armR!, foreR: arms.foreR!,
  }));
};
